import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import useManageTemplates from '../../_shared/_data_sources/useManageTemplates';
import { CreateTemplateRequest } from '../../_shared/_types';
import { useQueryClient } from 'react-query';
import { ServerErrorResponse } from 'data_sources/constants';
import TemplateForm from '../../_shared/_components/TemplateForm';
import { useTemplate } from '../../_shared/_data_sources/useTemplate';

const TemplateNew: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get('parentId');
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { createTemplate } = useManageTemplates();
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();

  // Fetch parent template if parentId is provided
  const parentTemplateQuery = useTemplate(parentId ? Number(parentId) : 0, { enabled: !!parentId }, (error) => {
    console.error('Error fetching parent template:', error);
  });

  const isChildMode = !!parentId;
  const parentTemplate = parentTemplateQuery.data;

  const handleCreate = async (formData: CreateTemplateRequest) => {
    await createTemplate(
      formData,
      (response) => {
        const successMessage = formData.parentId
          ? 'Child template created successfully'
          : 'Template created successfully';
        enqueueSnackbar(successMessage, {
          variant: 'success',
          autoHideDuration: 3000,
        });
        queryClient.invalidateQueries(['templates']);
        if (formData.parentId) {
          queryClient.invalidateQueries(['childTemplates', formData.parentId]);
        }
        navigate(`/admin/performance-reporting/templates/builder/${response.id}`);
      },
      (error) => {
        setServerErrors(error);
      }
    );
  };

  const handleCancel = () => {
    if (isChildMode && parentId) {
      navigate(`/admin/performance-reporting/templates/details/${parentId}#tab=childTemplates`);
    } else {
      navigate('/admin/performance-reporting/templates');
    }
  };

  // Check permissions and redirect if user doesn't have manage templates permission
  // useEffect(() => {
  //   if (!hasPermission('manageTemplates', currentOrganizationId || '')) {
  //     navigate('/admin/performance-reporting');
  //   }
  // }, [hasPermission, currentOrganizationId, navigate]);

  // Show loading state while fetching parent template
  if (isChildMode && parentTemplateQuery.isLoading) {
    return (
      <TakeoverLayout
        pageTitle={t('performanceReporting.templates.createChild.title')}
        backUrl={`/admin/performance-reporting/templates/details/${parentId}#tab=childTemplates`}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </TakeoverLayout>
    );
  }

  const pageTitle = isChildMode ? t('performanceReporting.templates.createChild.title') : 'Create Template';

  const backUrl =
    isChildMode && parentId
      ? `/admin/performance-reporting/templates/details/${parentId}#tab=childTemplates`
      : '/admin/performance-reporting/templates';

  return (
    <TakeoverLayout
      pageTitle={pageTitle}
      backUrl={backUrl}
    >
      <Box>
        <TemplateForm
          onCreate={handleCreate}
          onCancel={handleCancel}
          serverErrors={serverErrors}
          setServerErrors={setServerErrors}
          isChildMode={isChildMode}
          parentTemplate={parentTemplate}
        />
      </Box>
    </TakeoverLayout>
  );
};

export default TemplateNew;
